// src/Contexts/ActiveJobProvider.js
//
// The Firestore-backed half of ActiveJobContext. Mounted once in Routers.js,
// inside AuthProvider (it needs the uid).
//
// PERSISTENCE. The selected job is written to saved-jobs/{uid}.activeJob
// (see activeJobHelper for why that document), mirrored to localStorage for
// the first paint, and on hydrate refreshed from
// saved-jobs/{uid}/items/{jobId} when the job is tracked - the tracker's
// snapshot is the freshest copy of the posting.
//
// HYDRATE ORDER on sign-in / refresh:
//   1. localStorage mirror  -> shown immediately
//   2. Firestore pointer    -> wins when present
//   3. mirror, then the legacy Tailor hand-off cache -> adopted when Firestore
//      has nothing, and written back up so the next device sees it
//   4. the tracked item's snapshot -> refreshes whichever won
// A setActiveJob() that fires while the read is in flight is newer than
// anything the read returns and is kept.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import moment from "moment";

import { db } from "../firebase";
import { AuthContext } from "./AuthContext";
import { ActiveJobContext } from "./ActiveJobContext";
import constant from "../utils/constant";
import {
  ACTIVE_JOB_FIELD,
  ACTIVE_JOB_UPDATED_FIELD,
  clearActiveJobMirror,
  clearLegacyCoverLetterTarget,
  mergeActiveJob,
  normalizeActiveJob,
  readActiveJobMirror,
  readLegacyActiveJob,
  writeActiveJobMirror,
} from "../helper/jobs/activeJobHelper";

const { DB_NAME } = constant;

const SAVED_JOBS_COLLECTION = DB_NAME.SAVED_JOBS || "saved-jobs";
const SAVED_JOBS_ITEMS = "items";

const docRefFor = (uid) => db.collection(SAVED_JOBS_COLLECTION).doc(uid);

// Every Firestore write here is fire-and-forget: a failed pointer write must
// never break the hand-off that triggered it. The mirror already holds the
// job, so the current tab keeps working; only cross-device sync is lost.
const persistActiveJob = (uid, job) => {
  if (!uid) return Promise.resolve(false);

  const now = `${moment().format()}`;

  return docRefFor(uid)
    .set(
      {
        [ACTIVE_JOB_FIELD]: job || null,
        [ACTIVE_JOB_UPDATED_FIELD]: now,
      },
      { merge: true }
    )
    .then(() => true)
    .catch(() => false);
};

// The tracker's item is the freshest snapshot of a tracked posting (its
// description may have been filled in after the pointer was written). Falls
// back to the stored pointer when the job is untracked or the read fails.
const refreshFromSavedItem = async (uid, job) => {
  const jobId = String(get(job, "jobId", "") || "").trim();

  if (!uid || !jobId) return job;

  try {
    const doc = await docRefFor(uid)
      .collection(SAVED_JOBS_ITEMS)
      .doc(jobId)
      .get();

    if (!doc.exists) return job;

    const fresh = normalizeActiveJob({ ...doc.data(), jobId: doc.id });

    return fresh ? { ...fresh, setAt: get(job, "setAt", fresh.setAt) } : job;
  } catch (error) {
    return job;
  }
};

export const ActiveJobProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext) || {};
  const uid = get(currentUser, "uid", null);

  const [activeJob, setActiveJobState] = useState(null);
  const [isHydrating, setIsHydrating] = useState(Boolean(uid));
  // Guards the async hydrate against a sign-out / account switch mid-read,
  // and against a setActiveJob() that landed while the read was in flight.
  const hydrateTokenRef = useRef(0);
  const lastLocalWriteRef = useRef(0);

  useEffect(() => {
    hydrateTokenRef.current += 1;
    const token = hydrateTokenRef.current;
    // A new account starts clean: a hand-off made by the previous user must
    // not veto this user's hydrate.
    lastLocalWriteRef.current = 0;

    if (!uid) {
      setActiveJobState(null);
      setIsHydrating(false);
      return;
    }

    const mirrored = readActiveJobMirror(uid);
    setActiveJobState(mirrored);
    setIsHydrating(true);

    const hydrate = async () => {
      let resolved = null;
      let fromFirestore = false;

      try {
        const doc = await docRefFor(uid).get();
        resolved = normalizeActiveJob(get(doc.data(), ACTIVE_JOB_FIELD, null));
        fromFirestore = Boolean(resolved);
      } catch (error) {
        resolved = null;
      }

      if (!resolved) resolved = mirrored || readLegacyActiveJob();

      if (resolved) resolved = await refreshFromSavedItem(uid, resolved);

      if (token !== hydrateTokenRef.current) return;

      if (lastLocalWriteRef.current > 0) {
        setIsHydrating(false);
        return;
      }

      setActiveJobState(resolved);
      setIsHydrating(false);

      if (resolved) {
        writeActiveJobMirror(uid, resolved);
        if (!fromFirestore) persistActiveJob(uid, resolved);
      }
    };

    hydrate();
  }, [uid]);

  const setActiveJob = useCallback(
    (job) => {
      const normalized = normalizeActiveJob(job);

      if (!normalized) return null;

      lastLocalWriteRef.current = Date.now();

      // Re-asserting the same job (a page confirming what it was handed)
      // keeps the richer of the two snapshots: a card with a description
      // must not be replaced by a route-state copy without one.
      const next = mergeActiveJob(activeJob, normalized);

      setActiveJobState(next);
      writeActiveJobMirror(uid, next);
      persistActiveJob(uid, next);

      return next;
    },
    [uid, activeJob]
  );

  const clearActiveJob = useCallback(() => {
    lastLocalWriteRef.current = Date.now();
    setActiveJobState(null);
    clearActiveJobMirror(uid);
    clearLegacyCoverLetterTarget();
    persistActiveJob(uid, null);
  }, [uid]);

  const value = useMemo(
    () => ({
      activeJob,
      activeJobId: String(get(activeJob, "jobId", "") || ""),
      hasActiveJob: !isEmpty(activeJob),
      isHydrating,
      setActiveJob,
      clearActiveJob,
    }),
    [activeJob, isHydrating, setActiveJob, clearActiveJob]
  );

  return (
    <ActiveJobContext.Provider value={value}>
      {children}
    </ActiveJobContext.Provider>
  );
};

export default ActiveJobProvider;
